import { translations } from '@internal/i18n/translations';
import { localeNamespace } from '@internal/utils/machine/i18n/Locale';
import { RadioButtonGroup } from '@renderer-ui-library/atoms';
import { useFilterBarOptions } from '@renderer-ui-library/molecules';
import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Typography,
} from '@renderer-ui-library/mui';
import { useMediaQuery } from '@renderer-ui-library/mui/utils/mediaQuery/useMediaQuery';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useMemo } from 'react';
import { tracker } from 'utils/tracking/tracker';
import { useSearchParamsContext } from './SearchParamsContext';
import { SearchResultsSortKey } from './SearchResultsSortKey';
import styles from './searchResultsSorting.module.scss';
import { useSearchResultsRouting } from './useSearchResultsRouting';

interface Props {
    onChange?: () => void;
    closeMobileMenu: () => void;
}

export const SearchResultsSorting: React.FC<Props> = React.memo(props => {
    const { searchParams } = useSearchParamsContext();
    const { gotoSearchResults } = useSearchResultsRouting();
    const { t } = useTranslation(localeNamespace.common);
    const { isMobile } = useMediaQuery();
    const { options } = useFilterBarOptions();

    const handleChangeSort = useCallback(
        (sort: SearchResultsSortKey) => {
            props.onChange?.();

            tracker.trackEvent({
                name: 'machine-sorting',
                machine_sorting_order: sort,
                is_interaction: true,
            });

            gotoSearchResults({
                ...searchParams,
                sort,
                replace: true,
            });
        },
        [props, gotoSearchResults, searchParams],
    );

    const handleSelectSort = useCallback(
        (event: SelectChangeEvent<SearchResultsSortKey>) => {
            const sortKey = event.target.value as SearchResultsSortKey;
            handleChangeSort(sortKey);
        },
        [handleChangeSort],
    );

    const handleRadioSelectSort = useCallback(
        (sort: string) => {
            handleChangeSort(sort as SearchResultsSortKey);
            props.closeMobileMenu();
        },
        [handleChangeSort, props],
    );

    const disabled = useMemo(
        () => !!searchParams.models?.some(m => m.type === 'fuzzy'),
        [searchParams.models],
    );

    return (
        <>
            {isMobile ? (
                <RadioButtonGroup
                    options={options}
                    value={searchParams.sort}
                    onChange={handleRadioSelectSort}
                    disabled={disabled}
                />
            ) : (
                <div className={styles.container}>
                    <FormControl fullWidth sx={{ width: '250px' }} size="small">
                        <InputLabel id="popover-sort-label">
                            {t(translations.searchResultsSortingDescription)}
                        </InputLabel>
                        <Select
                            id="popover-sort"
                            labelId="popover-sort-label"
                            displayEmpty
                            label={t(
                                translations.searchResultsSortingDescription,
                            )}
                            value={searchParams.sort ?? ''}
                            variant="outlined"
                            onChange={handleSelectSort}
                            disabled={disabled}
                        >
                            {options.map(option => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        component="span"
                                    >
                                        {option.label}
                                    </Typography>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
            )}
        </>
    );
});

SearchResultsSorting.displayName = 'SearchResultsSorting';
